// src/services/organization/OrganizationService.ts
import { BaseTenantCrudService } from '@/services/_base/BaseTenantCrudService'
import { type Organization } from '@/types/Organization.ts'
import type { IFirestoreService } from '@/platform/firebase/firestore'

type Session = { tenantId?: string; yardId?: string; userId?: string }

export class OrganizationService extends BaseTenantCrudService<Organization> {
  constructor(firestore: IFirestoreService, session: Session) {
    super('organizations', firestore, session)
  }

  protected override beforeCreate(data: Partial<Organization>): Partial<Organization> {
    return {
      ...super.beforeCreate(data),
      name_lower: (data.name ?? '').toLowerCase(),
    }
  }

  protected override beforeUpdate(id: string, data: Partial<Organization>): Partial<Organization> {
    const normalized = { ...super.beforeUpdate(id, data) }

    if (data.name) {
      normalized.name_lower = data.name.toLowerCase()
    }

    return normalized
  }

  protected override async afterCreate(id: string, organization: Partial<Organization>) {
    // e.g., index to CRM timeline or trigger a welcome email
    console.log(`[OrganizationService] Created organization ${id}`, organization)
  }

  /** Soft delete an organization by setting isDeleted flag */
  async softDelete(id: string): Promise<void> {
    await this.update(id, { isDeleted: true } as Partial<Organization>)
  }

  /** Update the last contact date for an organization */
  async updateLastContact(id: string): Promise<void> {
    const now = new Date()
    await this.update(id, { lastContactedAt: now } as Partial<Organization>)
  }

  /** Archive an organization */
  async archive(id: string): Promise<void> {
    await this.update(id, {
      status: 'archived',
      updatedAt: new Date(),
    } as Partial<Organization>)
  }

  /** Set the next follow-up date for an organization */
  async setNextFollowUpDate(id: string, date: Date): Promise<void> {
    await this.update(id, {
      nextFollowUpDate: date,
      updatedAt: new Date(),
    } as Partial<Organization>)
  }

  /** Update organization with normalization of name fields */
  async updateWithNormalization(id: string, data: Partial<Organization>): Promise<void> {
    await this.update(id, data)
  }

  /** Get organizations that need follow-up */
  async getOrganizationsNeedingFollowUp(daysCutoff = 30): Promise<Organization[]> {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysCutoff)

    // This would typically use a search service, but for now we'll use a direct query
    const results = await this.firestore.runQuery({
      collection: this.getCollectionPath(),
      wheres: [
        { field: 'lastContactedAt', op: '<', value: cutoffDate },
        { field: 'isDeleted', op: '==', value: false },
      ],
      orders: [{ field: 'lastContactedAt', dir: 'asc' }],
      meta: { name: 'organizations.needingFollowUp' },
    })

    return results as Organization[]
  }

  /** Get organizations with upcoming follow-up dates */
  async getOrganizationsWithUpcomingFollowUp(): Promise<Organization[]> {
    const now = new Date()

    const results = await this.firestore.runQuery({
      collection: this.getCollectionPath(),
      wheres: [
        { field: 'nextFollowUpDate', op: '<=', value: now },
        { field: 'isDeleted', op: '==', value: false },
      ],
      orders: [{ field: 'nextFollowUpDate', dir: 'asc' }],
      meta: { name: 'organizations.upcomingFollowUp' },
    })

    return results as Organization[]
  }

  /** Search organizations by name, email, or phone */
  async searchOrganizations(query: string, limit = 25): Promise<Organization[]> {
    const searchTerm = query.toLowerCase()

    // Try name prefix search first
    const nameResults = await this.firestore.runQuery({
      collection: this.getCollectionPath(),
      wheres: [
        { field: 'name_lower', op: '>=', value: searchTerm },
        { field: 'name_lower', op: '<=', value: searchTerm + '\uf8ff' },
      ],
      orders: [{ field: 'name_lower', dir: 'asc' }],
      limit,
      meta: { name: 'organizations.searchByName' },
    })

    return nameResults as Organization[]
  }
}
